"use client";
import React, { type ReactNode, useMemo, useRef, useState } from "react";
import type { ComboBoxOptionType, DropdownOptionType, TooltipProps } from ".";
import ColorInput from "./ColorInput";
import HexColorPicker from "./color-picker/HexColorPicker";
import { PropertyType } from "@/tailwind/tailwindUtils";

import type { NewPropertyState } from "@/components/RightStyle/usePropertyStateV2";
import {
  COLOUR_FALLBACK,
  isCssColorFunction,
  normalizeColorValue,
  parseColorValue,
} from "./color-picker/utils/colorFormatUtils";

interface ColorInputProps extends React.HTMLAttributes<HTMLDivElement> {
  propertyType?: PropertyType; // Optional for backward compatibility
  propertyState: Partial<NewPropertyState> & {
    propertyValue: string;
    displayValue: string;
    isSet: boolean;
  };

  comboboxOptions: ComboBoxOptionType[];
  setValue: (option: ComboBoxOptionType) => void; // Called when interaction is complete
  instantUpdateValue: (option: ComboBoxOptionType) => void; // Called during dragging for real-time updates
  buildArbitraryClass: (input: string) => ComboBoxOptionType;

  dropdownOptions?: DropdownOptionType[];
  onDropdownSelected?: (option: DropdownOptionType) => void;
  buildOptionPreview?: (option: ComboBoxOptionType) => ReactNode;
  onInputFocus?: (event: React.FocusEvent<HTMLInputElement, Element>) => void;
  disabled?: boolean;
  onClear: () => void;
  showHex: boolean;
  onOpenVariantModal?: () => void;
  variantAnchorRef?: React.RefObject<HTMLButtonElement>;
  onBeforeSetValue?: () => void;
  forceUpdate?: boolean; // Flag to force inputValue update regardless of existing value
}

const CustomColorPicker: React.FC<ColorInputProps & TooltipProps> = ({
  propertyType = PropertyType.BACKGROUND_COLOR, // Default to background color for backward compatibility
  propertyState,
  comboboxOptions,
  setValue,
  instantUpdateValue,
  buildArbitraryClass,
  buildOptionPreview,
  onInputFocus,
  disabled = false,
  tooltipContent = null,
  onClear,
  dropdownOptions = [],
  onDropdownSelected,
  showHex = true,
  onOpenVariantModal,
  variantAnchorRef,
  onBeforeSetValue,
  forceUpdate = false,
  ...restProps
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const lastColorValueRef = useRef<string>("");
  const dragStartValueRef = useRef<string>("");
  const [currentColorValue, setCurrentColorValue] = useState<string>("");

  const colorPickerValue = useMemo(() => {
    // During dragging, always prefer the current dragging color
    if (
      isDragging &&
      currentColorValue &&
      currentColorValue !== COLOUR_FALLBACK
    ) {
      return currentColorValue;
    }

    // If we have a current color value from dragging (not cleared yet), use it
    if (currentColorValue && currentColorValue !== COLOUR_FALLBACK) {
      return currentColorValue;
    }

    // Get value from propertyState - prioritize displayValue over propertyValue
    const colorOptions = [
      propertyState.displayValue,
      propertyState.propertyValue,
      propertyState.cssValue,
    ]
      .filter((option) => {
        return option && option !== COLOUR_FALLBACK && option !== "1";
      })
      .map((option) => {
        const parsedColor = parseColorValue(option || "", comboboxOptions);

        if (parsedColor.isValid && parsedColor.hex !== COLOUR_FALLBACK) {
          return parsedColor.hex;
        }

        return null;
      })
      .filter(Boolean);

    // Only fallback to default if we truly have no color
    return colorOptions?.[0] || COLOUR_FALLBACK;
  }, [
    isDragging,
    currentColorValue,
    propertyState.displayValue,
    propertyState.propertyValue,
    propertyState.cssValue,
    comboboxOptions,
  ]);

  // drag tracking for when the mouse ends up over an iframe
  React.useEffect(() => {
    if (!isDragging) return;

    const handleEnd = () => {
      // Only call setValue if the color actually changed during dragging
      if (
        lastColorValueRef.current &&
        lastColorValueRef.current !== dragStartValueRef.current
      ) {
        // Final setValue call to commit the change
        // This will call pickTailwindClassForProperty to make the persistent change
        setValue(buildArbitraryClass(lastColorValueRef.current));
      }

      // Reset drag state
      setIsDragging(false);
      dragStartValueRef.current = "";
      lastColorValueRef.current = "";
      // Don't clear currentColorValue immediately - let the clearing effect handle it
      // This prevents flashing during the transition
    };

    // Listen for drag end events
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging, setValue, buildArbitraryClass]);

  // Clear currentColorValue when drag ends and property state has been updated
  React.useEffect(() => {
    // Only clear if we're not currently dragging and have a current color value
    if (
      !isDragging &&
      currentColorValue &&
      currentColorValue !== COLOUR_FALLBACK
    ) {
      // Use a timeout to allow property state to update after drag end
      const timeoutId = setTimeout(() => {
        const propertyValue =
          propertyState.propertyValue || propertyState.displayValue;

        if (propertyValue && propertyValue !== COLOUR_FALLBACK) {
          const parsedColor = parseColorValue(propertyValue, comboboxOptions);
          const propertyHex = parsedColor.isValid
            ? parsedColor.hex
            : propertyValue;

          // If the property state now matches our current color value, we can clear it
          // Use a more lenient comparison to handle different color formats
          const normalizedCurrent = normalizeColorValue(currentColorValue);
          const normalizedProperty = normalizeColorValue(propertyHex);

          if (
            normalizedCurrent === normalizedProperty ||
            propertyHex === currentColorValue ||
            propertyHex.toLowerCase() === currentColorValue.toLowerCase()
          ) {
            setCurrentColorValue("");
          }
        }
      }, 50); // Small delay to let property state update

      return () => clearTimeout(timeoutId);
    }
  }, [
    isDragging,
    currentColorValue,
    propertyState.propertyValue,
    propertyState.displayValue,
    comboboxOptions,
  ]);

  return (
    <div className="w-full" {...restProps}>
      <HexColorPicker
        id="hex-color-picker"
        color={colorPickerValue}
        onChange={(color) => {
          const normalizedColor = normalizeColorValue(color);

          // Only update if the color actually changed to prevent unnecessary re-renders
          if (normalizedColor !== lastColorValueRef.current) {
            lastColorValueRef.current = normalizedColor;
            setCurrentColorValue(normalizedColor);

            // Start drag tracking on first change
            if (!isDragging) {
              dragStartValueRef.current = colorPickerValue;
              setIsDragging(true);
            }

            const arbitraryClass = buildArbitraryClass(normalizedColor);
            instantUpdateValue(arbitraryClass);
          }
        }}
        onPointerDown={restProps.onPointerDown}
      />
      {showHex === true && (
        <ColorInput
          alwaysShowHex={true}
          propertyType={propertyType}
          propertyState={propertyState}
          comboboxOptions={comboboxOptions}
          setValue={setValue}
          instantUpdateValue={instantUpdateValue}
          buildArbitraryClass={buildArbitraryClass}
          dropdownOptions={dropdownOptions}
          onDropdownSelected={onDropdownSelected}
          buildOptionPreview={buildOptionPreview}
          onInputFocus={onInputFocus}
          disabled={disabled}
          onClear={onClear}
          onBeforeSetValue={onBeforeSetValue}
          forceUpdate={forceUpdate}
          externalValue={colorPickerValue}
        />
      )}
    </div>
  );
};

export default React.memo(CustomColorPicker);
